import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LandingPage from './components/LandingPage/LandingPage';
import AuthPage from './components/Auth/AuthPage';
import ProfileQuestionnaire from './components/ProfileQuestionnaire';
import HomePage from './components/Home/HomePage';
import TrainingPlanView from './components/TrainingPlan/TrainingPlanView';
import SettingsModal from './components/Settings/SettingsModal';
import CommunityDropdown from './components/Community/CommunityDropdown';
import GlobalSearch from './components/GlobalSearch/GlobalSearch';
import ChallengesPage from './components/Challenges/ChallengesPage';
import ClubsPage from './components/Clubs/ClubsPage';
import RacesPage from './components/Races/RacesPage';
import MessagesPage from './components/Messages/MessagesPage';
import MessageIcon from './components/Messages/MessageIcon';
import WorkoutTemplatesTest from './components/WorkoutTemplatesTest';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AppContent() {
  const { user, isAuthenticated, loading, logout, authFetch, updateUserData } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [trainingPlan, setTrainingPlan] = useState(null);
  const [isLoadingData] = useState(false);
  const [showAuthPage, setShowAuthPage] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [currentView, setCurrentView] = useState('home');
  const [showSettings, setShowSettings] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [pendingConversationUser, setPendingConversationUser] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [showTestPage, setShowTestPage] = useState(null);

  // Check URL for test page parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');
    if (testParam === 'workouts') {
      setShowTestPage('workouts');
    }
  }, []);

  // Check URL for password reset token on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token && window.location.pathname.includes('reset-password')) {
      setResetToken(token);
      setShowAuthPage(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  // Scroll to top on view change or page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView]);

  // Handle Garmin OAuth callback - wait for auth to complete first
  useEffect(() => {
    // Don't process callback while auth is still loading
    if (loading) return;

    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    if (path === '/connect/garmin') {
      const success = urlParams.get('success');
      const garminUserId = urlParams.get('user_id');
      const error = urlParams.get('error');

      console.log('Garmin OAuth callback:', { success, garminUserId, error, isAuthenticated });

      if (success === 'true' && garminUserId) {
        // Save Garmin user ID to localStorage so GarminConnect component can use it
        localStorage.setItem('garmin_user_id', garminUserId);
        console.log('Garmin connected successfully! User ID saved to localStorage.');
        // Redirect to home - GarminConnect will pick up the saved user ID
        window.history.replaceState({}, document.title, '/');
      } else if (error) {
        console.error('Garmin connection error:', error);
        localStorage.setItem('garmin_error', error);
        window.history.replaceState({}, document.title, '/');
      }
    }
  }, [loading, isAuthenticated]);

  // Handle Whoop OAuth callback - wait for auth to complete first
  useEffect(() => {
    // Don't process callback while auth is still loading
    if (loading) return;

    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    if (path === '/connect/whoop') {
      const success = urlParams.get('success');
      const whoopUserId = urlParams.get('user_id');
      const error = urlParams.get('error');

      console.log('Whoop OAuth callback:', { success, whoopUserId, error, isAuthenticated });

      if (success === 'true' && whoopUserId) {
        // Save Whoop user ID to localStorage so WhoopConnect component can use it
        localStorage.setItem('whoop_user_id', whoopUserId);
        console.log('Whoop connected successfully! User ID saved to localStorage.');
        // Redirect to home
        window.history.replaceState({}, document.title, '/');
      } else if (error) {
        console.error('Whoop connection error:', error);
        localStorage.setItem('whoop_error', error);
        window.history.replaceState({}, document.title, '/');
      }
    }
  }, [loading, isAuthenticated]);

  // Handle Oura OAuth callback - wait for auth to complete first
  useEffect(() => {
    if (loading) return;

    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    if (path === '/connect/oura') {
      const success = urlParams.get('success');
      const ouraUserId = urlParams.get('user_id');
      const error = urlParams.get('error');

      console.log('Oura OAuth callback:', { success, ouraUserId, error, isAuthenticated });

      if (success === 'true' && ouraUserId) {
        localStorage.setItem('oura_user_id', ouraUserId);
        console.log('Oura connected successfully! User ID saved to localStorage.');
        window.history.replaceState({}, document.title, '/');
      } else if (error) {
        console.error('Oura connection error:', error);
        localStorage.setItem('oura_error', error);
        window.history.replaceState({}, document.title, '/');
      }
    }
  }, [loading, isAuthenticated]);

  // Fetch unread message count
  const fetchUnreadMessageCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await authFetch('/api/messages/unread-count');
      if (res.ok) {
        const data = await res.json();
        setUnreadMessageCount(data.count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [isAuthenticated, authFetch]);

  // Poll for unread messages
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchUnreadMessageCount();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadMessageCount();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadMessageCount]);

  // Load saved profile and plan when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      // Reset showAuthPage when authenticated
      setShowAuthPage(false);
      // Check if user has existing profile/plan from login response
      if (user.profile) {
        setUserProfile(user.profile);
      }
      if (user.active_plan) {
        setTrainingPlan(user.active_plan.plan_data);
      }
    }
  }, [isAuthenticated, user]);

  const handleProfileComplete = async (data) => {
    // Data contains: { profile, vdot, plan }
    setUserProfile(data.profile);
    setTrainingPlan(data.plan);

    // Save profile and plan to backend if authenticated
    if (isAuthenticated) {
      try {
        // Save profile
        await authFetch('/api/user/profile', {
          method: 'PUT',
          body: JSON.stringify({
            name: `${data.profile.firstName} ${data.profile.lastName}`,
            date_of_birth: data.profile.dateOfBirth,
            gender: data.profile.gender,
            training_level: data.profile.experienceLevel,
            goal_race: data.profile.goalRace,
            goal_race_date: data.profile.goalRaceDate,
            long_run_day: data.profile.longRunDay,
            recent_race_distance: data.profile.recentRaceDistance,
            recent_race_time: data.profile.recentRaceTime,
            fitness_level: data.vdot,
            personal_bests: data.profile.personalBests,
            weight: data.profile.weight,
            height: data.profile.height,
            unit_system: data.profile.unitSystem,
            preferred_units: data.profile.preferredUnits
          })
        });

        // Save training plan
        await authFetch('/api/user/training-plan', {
          method: 'POST',
          body: JSON.stringify({
            plan_data: data.plan
          })
        });

        // Update user state
        updateUserData({
          has_profile: true,
          has_active_plan: true
        });
      } catch (err) {
        console.error('Failed to save data:', err);
        // Data is still displayed locally even if save fails
      }
    }
  };

  const handleLogout = () => {
    logout();
    setUserProfile(null);
    setTrainingPlan(null);
    setShowAuthPage(false);
  };

  const handleGetStarted = () => {
    setAuthMode('register');
    setShowAuthPage(true);
  };

  const handleLogin = () => {
    setAuthMode('login');
    setShowAuthPage(true);
  };

  const handleSignUp = () => {
    setAuthMode('register');
    setShowAuthPage(true);
  };

  const handleBackToLanding = () => {
    setShowAuthPage(false);
    setResetToken(null);
  };

  const handleProfileUpdate = (updatedProfile) => {
    setUserProfile(updatedProfile);
  };

  const handlePlanUpdate = (updatedPlan) => {
    setTrainingPlan(updatedPlan);
  };

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="App loading-screen">
        <img src="/logo.png" alt="AIthlete" className="loading-logo" />
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Show landing page or auth page if not authenticated
  if (!isAuthenticated) {
    if (showAuthPage || resetToken) {
      return <AuthPage onBack={handleBackToLanding} initialMode={authMode} resetToken={resetToken} />;
    }
    return <LandingPage onGetStarted={handleGetStarted} onLogin={handleLogin} onSignUp={handleSignUp} />;
  }

  // Show loading while fetching user data
  if (isLoadingData) {
    return (
      <div className="App loading-screen">
        <img src="/logo.png" alt="AIthlete" className="loading-logo" />
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Show test page if requested via URL param ?test=workouts
  if (showTestPage === 'workouts') {
    return (
      <WorkoutTemplatesTest
        onClose={() => {
          setShowTestPage(null);
          window.history.replaceState({}, document.title, '/');
        }}
      />
    );
  }

  // Main app content
  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/icon_logo.png" alt="AIthlete" className="header-logo-img" />
            {trainingPlan && (
              <nav className="header-nav">
                <button
                  className={`nav-link ${currentView === 'home' ? 'active' : ''}`}
                  onClick={() => setCurrentView('home')}
                >
                  Home
                </button>
                <button
                  className={`nav-link ${currentView === 'plan' ? 'active' : ''}`}
                  onClick={() => setCurrentView('plan')}
                >
                  Plan
                </button>
                <CommunityDropdown currentView={currentView} onViewChange={setCurrentView} />
              </nav>
            )}
          </div>
          <div className="header-right">
            {trainingPlan && (
              <MessageIcon
                onClick={() => setCurrentView('messages')}
                unreadCount={unreadMessageCount}
              />
            )}
            {trainingPlan && (
              <GlobalSearch
                onNavigate={setCurrentView}
                onStartConversation={(user) => {
                  setPendingConversationUser(user);
                  setCurrentView('messages');
                }}
              />
            )}
            <div className="profile-menu-container">
            <button
              className={`profile-avatar ${userProfile?.profile_image ? 'has-image' : ''}`}
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              title={user?.email}
            >
              {userProfile?.profile_image ? (
                <img
                  src={userProfile.profile_image.startsWith('http')
                    ? userProfile.profile_image
                    : `${API_BASE_URL}${userProfile.profile_image}`}
                  alt="Profile"
                  className="profile-avatar-img"
                />
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              )}
            </button>
            {showProfileMenu && (
              <>
                <div className="profile-menu-backdrop" onClick={() => setShowProfileMenu(false)} />
                <div className="profile-menu">
                  <div className="profile-menu-header">
                    <span className="profile-menu-email">{user?.email}</span>
                    {userProfile?.name && <span className="profile-menu-name">{userProfile.name}</span>}
                  </div>
                  <div className="profile-menu-divider" />
                  <button
                    className="profile-menu-item"
                    onClick={() => {
                      setShowSettings(true);
                      setShowProfileMenu(false);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    Settings
                  </button>
                  <button
                    className="profile-menu-item logout"
                    onClick={() => {
                      handleLogout();
                      setShowProfileMenu(false);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </header>
      {/* Mobile top bar — visible only on mobile */}
      {trainingPlan && (
        <div className="mobile-top-bar">
          <div className="mobile-top-bar-left">
            <div className="profile-menu-container">
              <button
                className={`profile-avatar ${userProfile?.profile_image ? 'has-image' : ''}`}
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                title={user?.email}
              >
                {userProfile?.profile_image ? (
                  <img
                    src={userProfile.profile_image.startsWith('http')
                      ? userProfile.profile_image
                      : `${API_BASE_URL}${userProfile.profile_image}`}
                    alt="Profile"
                    className="profile-avatar-img"
                  />
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                )}
              </button>
              {showProfileMenu && (
                <>
                  <div className="profile-menu-backdrop" onClick={() => setShowProfileMenu(false)} />
                  <div className="profile-menu profile-menu--mobile">
                    <div className="profile-menu-header">
                      <span className="profile-menu-email">{user?.email}</span>
                      {userProfile?.name && <span className="profile-menu-name">{userProfile.name}</span>}
                    </div>
                    <div className="profile-menu-divider" />
                    <button
                      className="profile-menu-item"
                      onClick={() => { setShowSettings(true); setShowProfileMenu(false); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      Settings
                    </button>
                    <button
                      className="profile-menu-item logout"
                      onClick={() => { handleLogout(); setShowProfileMenu(false); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="mobile-top-bar-right">
            <button
              className={`mobile-top-icon ${currentView === 'messages' ? 'active' : ''}`}
              onClick={() => setCurrentView('messages')}
              title="Messages"
              style={{ position: 'relative' }}
            >
              <svg viewBox="0 0 28 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="22">
                <rect x="10" y="2" width="16" height="12" rx="3" ry="3"/>
                <polyline points="14,14 12,18 18,14"/>
                <rect x="2" y="6" width="16" height="12" rx="3" ry="3"/>
                <polyline points="6,18 4,22 10,18"/>
              </svg>
              {unreadMessageCount > 0 && (
                <span className="tab-badge">{unreadMessageCount > 9 ? '9+' : unreadMessageCount}</span>
              )}
            </button>
            <GlobalSearch
              onNavigate={setCurrentView}
              onStartConversation={(user) => {
                setPendingConversationUser(user);
                setCurrentView('messages');
              }}
            />
          </div>
        </div>
      )}

      <main className="app-main">
        {!trainingPlan ? (
          <ProfileQuestionnaire onComplete={handleProfileComplete} />
        ) : currentView === 'home' ? (
          <HomePage
            trainingPlan={trainingPlan}
            profile={userProfile}
            onPlanUpdate={handlePlanUpdate}
            onViewChange={setCurrentView}
          />
        ) : currentView === 'challenges' ? (
          <ChallengesPage />
        ) : currentView === 'clubs' ? (
          <ClubsPage />
        ) : currentView === 'races' ? (
          <RacesPage />
        ) : currentView === 'messages' ? (
          <MessagesPage
            onUnreadCountChange={setUnreadMessageCount}
            pendingConversationUser={pendingConversationUser}
            onClearPendingUser={() => setPendingConversationUser(null)}
          />
        ) : (
          <TrainingPlanView
            trainingPlan={trainingPlan}
            profile={userProfile}
            onPlanUpdate={handlePlanUpdate}
          />
        )}
      </main>

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        profile={userProfile}
        trainingPlan={trainingPlan}
        onProfileUpdate={handleProfileUpdate}
        onTrainingPlanUpdate={handlePlanUpdate}
      />

      {trainingPlan && (
        <nav className="mobile-tab-bar">
          <button
            className={`tab-item ${currentView === 'home' ? 'active' : ''}`}
            onClick={() => setCurrentView('home')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <span>Home</span>
          </button>
          <button
            className={`tab-item ${currentView === 'plan' ? 'active' : ''}`}
            onClick={() => setCurrentView('plan')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </svg>
            <span>Plan</span>
          </button>
          <button
            className={`tab-item ${['challenges'].includes(currentView) ? 'active' : ''}`}
            onClick={() => setCurrentView('challenges')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z"/>
            </svg>
            <span>Challenges</span>
          </button>
          <button
            className={`tab-item ${currentView === 'clubs' ? 'active' : ''}`}
            onClick={() => setCurrentView('clubs')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <span>Groups/Clubs</span>
          </button>
          <button
            className={`tab-item ${currentView === 'races' ? 'active' : ''}`}
            onClick={() => setCurrentView('races')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z"/>
            </svg>
            <span>Races</span>
          </button>
        </nav>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
